import * as React from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ArgonBox from 'components/ArgonBox';
import ArgonButton from 'components/ArgonButton';
import { Grid, Tooltip } from '@mui/material';
import ArgonTypography from 'components/ArgonTypography';

import { useArgonController } from 'context';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import FormikNumberInput from 'components/FormikNumberInput';
import FormikCurrencyInput from 'components/FormikCurrencyInput';
import ArgonDatePicker from 'components/ArgonDatePicker';
import { Alert } from '@mui/material';
import ArgonCurrencyInput from 'components/ArgonCurrencyInput';
import { getSystemFeeForProductType } from 'services/REST_API/orders';

function BootstrapDialogTitle(props) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired
};

export default function SingleAdaptivePricingDialog({
  open,
  onClose,
  onSave,
  onDelete,
  existingData = null,
  currency = 'EUR',
  defaultPrice = 0,
  isEdit = false,
  existingPricePoints = [], // Add this to check if there are other price points
  event = null, // Add event prop for system fee calculation
  eventInstanceId = null // Add eventInstanceId prop for system fee calculation
}) {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [controller] = useArgonController();
  const { darkMode } = controller;

  // State for calculated system fee
  const [calculatedSystemFee, setCalculatedSystemFee] = React.useState(0);

  // Check if there's already a tier with fromRegistrations = 0
  const hasZeroRegistrationTier = existingPricePoints.some((point) => {
    // Skip if this is the same item we're editing (using same matching criteria as update)
    if (
      existingData &&
      point.fromRegistrations === existingData.fromRegistrations &&
      point.fromDate === existingData.fromDate &&
      point.registrationFee === existingData.registrationFee
    ) {
      return false;
    }
    return point.fromRegistrations === 0;
  });

  // Plausibility check utility functions
  const hasDuplicateRegistrationCount = (value) => {
    if (value === null || value === undefined) return false;
    return existingPricePoints.some((point) => {
      // Skip if this is the same item we're editing (using same matching criteria as update)
      if (
        existingData &&
        point.fromRegistrations === existingData.fromRegistrations &&
        point.fromDate === existingData.fromDate &&
        point.registrationFee === existingData.registrationFee
      ) {
        return false;
      }
      return point.fromRegistrations === value;
    });
  };

  const hasDuplicatePricePoint = (fromRegistrations, fromDate) => {
    return existingPricePoints.some((point) => {
      // Skip if this is the same item we're editing (using same matching criteria as update)
      if (
        existingData &&
        point.fromRegistrations === existingData.fromRegistrations &&
        point.fromDate === existingData.fromDate &&
        point.registrationFee === existingData.registrationFee
      ) {
        return false;
      }

      // Check for exact match of both registration count and date
      const sameRegistrations = point.fromRegistrations === fromRegistrations;
      const sameDate =
        (!point.fromDate && !fromDate) ||
        (point.fromDate &&
          fromDate &&
          new Date(point.fromDate).getTime() === new Date(fromDate).getTime());

      console.log('Duplicate check:', {
        point,
        fromRegistrations,
        fromDate,
        sameRegistrations,
        sameDate,
        pointFromDate: point.fromDate,
        inputFromDate: fromDate
      });

      return sameRegistrations && sameDate;
    });
  };

  const isUnreachableTier = (value) => {
    if (value === null || value === undefined) return false;

    // In adaptive pricing, higher tiers override lower tiers when their conditions are met
    // A tier is only unreachable if there's an identical registration count (handled by duplicate check)
    // or if there are logical impossibilities in the tier structure

    // For now, we don't have any cases where a tier would be truly unreachable
    // since higher registration counts override lower ones in adaptive pricing
    return false;
  };

  const isPastDate = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    return new Date(date) < today;
  };

  // Calculate system fee function (similar to Parameters.js)
  const calculateSystemFee = React.useCallback(
    async (registrationFee) => {
      console.log('Calculating system fee', {
        productAmount: registrationFee,
        productType: 'registration',
        subType: event?.type,
        organizationId: event?.organization?._id,
        eventInstanceId: eventInstanceId
      });

      let calculatedSystemFee = 0;

      if (Number(registrationFee) > 0) {
        await getSystemFeeForProductType({
          productAmount: Math.round(registrationFee * 100),
          productType: 'registration',
          subType: event?.type,
          organizationId: event?.organization?._id,
          eventInstanceId: eventInstanceId
        })?.then((res) => {
          calculatedSystemFee = res?.systemFee / 100;
        });
      }

      console.log('Calculated system fee', calculatedSystemFee.toFixed(2));

      setCalculatedSystemFee(calculatedSystemFee.toFixed(2));
    },
    [event, eventInstanceId]
  );

  // Create validation schema for single adaptive pricing entry
  const validationSchema = Yup.object({
    fromRegistrations: Yup.number()
      .min(0, 'Must be 0 or greater')
      .integer('Must be a whole number')
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value
      )
      .test(
        'no-duplicate-registration-count',
        'Another tier already uses this registration count',
        function (value) {
          return !hasDuplicateRegistrationCount(value);
        }
      )
      .test(
        'reachable-tier-warning',
        'This tier may never be reached due to existing tier with fromRegistrations = 0',
        function (value) {
          if (isUnreachableTier(value)) {
            // This is a warning, not a blocking error - we'll handle it in the UI
            return this.createError({
              path: 'fromRegistrations',
              message:
                'Warning: This tier may never be reached due to existing tier coverage'
            });
          }
          return true;
        }
      ),
    fromDate: Yup.date()
      .nullable()
      .typeError('Invalid date')
      .test(
        'no-date-with-zero-registration-conflict',
        'Cannot set a date when registration count is 0 and another tier already starts from 0 registrations',
        function (value) {
          const { fromRegistrations } = this.parent;

          // If fromRegistrations is 0 and there's already another tier with fromRegistrations = 0,
          // don't allow setting a date as this creates ambiguous pricing
          if (fromRegistrations === 0 && hasZeroRegistrationTier && value) {
            return false;
          }

          return true;
        }
      )
      .test(
        'not-past-date',
        'Warning: Date is in the past - this tier may not be useful for future pricing',
        function (value) {
          if (isPastDate(value)) {
            // This is a warning - we'll show it in UI but not block submission
            return this.createError({
              path: 'fromDate',
              message: 'Warning: Date is in the past'
            });
          }
          return true;
        }
      ),
    registrationFee: Yup.number()
      .required('Required')
      .min(0, 'Registration fee must be 0 or greater')
  })
    .test(
      'at-least-one-condition',
      'Either registration count or date must be specified',
      function (values) {
        const { fromRegistrations, fromDate } = values;

        // At least one of fromRegistrations or fromDate must be provided
        const hasRegistrations =
          fromRegistrations !== null &&
          fromRegistrations !== undefined &&
          fromRegistrations >= 0;
        const hasDate = fromDate !== null && fromDate !== undefined;

        if (!hasRegistrations && !hasDate) {
          return this.createError({
            path: 'fromRegistrations',
            message: 'Either registration count or date must be specified'
          });
        }

        return true;
      }
    )
    .test(
      'no-duplicate-price-point',
      'This exact price point configuration already exists',
      function (values) {
        const { fromRegistrations, fromDate } = values;

        if (hasDuplicatePricePoint(fromRegistrations, fromDate)) {
          return this.createError({
            path: 'fromRegistrations',
            message:
              'A price point with this exact registration count and date combination already exists'
          });
        }

        return true;
      }
    );

  // Initialize useFormik hook
  const formik = useFormik({
    initialValues: {
      fromRegistrations:
        existingData?.fromRegistrations ??
        (isEdit ? null : existingPricePoints.length === 0 ? 0 : null),
      fromDate: existingData?.fromDate || null,
      registrationFee: existingData?.registrationFee
        ? existingData.registrationFee / 100
        : defaultPrice / 100
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      console.log('Form submission - values:', values);
      console.log('Form submission - existingData:', existingData);
      try {
        // Convert registration fee from euros to cents for database storage
        const valuesForDatabase = {
          ...values,
          registrationFee: Math.round(values.registrationFee * 100)
        };
        await onSave(valuesForDatabase, existingData);
        enqueueSnackbar(
          isEdit
            ? 'Adaptive pricing updated successfully!'
            : 'Adaptive pricing added successfully!',
          { variant: 'success' }
        );
        handleClose();
      } catch (error) {
        console.error('Error saving adaptive pricing:', error);
        enqueueSnackbar('Error saving adaptive pricing. Please try again.', {
          variant: 'error'
        });
      }
    }
  });

  // Check if this is the first entry in an empty list
  const isFirstEntry = !isEdit && existingPricePoints.length === 0;

  // Check if this is or will be the first tier (fromRegistrations = 0)
  const isFirstTier = formik.values.fromRegistrations === 0 || isFirstEntry;

  // Use effect to calculate system fee when registration fee changes
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculateSystemFee(formik.values.registrationFee);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formik.values.registrationFee, calculateSystemFee]);

  // Calculate system fee on initial load when dialog opens
  React.useEffect(() => {
    if (open && formik.values.registrationFee) {
      calculateSystemFee(formik.values.registrationFee);
    }
  }, [open]);

  // Clear date when user sets fromRegistrations to 0
  React.useEffect(() => {
    if (isFirstTier && formik.values.fromDate) {
      formik.setFieldValue('fromDate', null);
    }
  }, [isFirstTier]);

  const handleClose = () => {
    onClose && onClose();
  };

  const handleDelete = async () => {
    try {
      await onDelete(existingData);
      enqueueSnackbar('Adaptive pricing deleted successfully!', {
        variant: 'success'
      });
      handleClose();
    } catch (error) {
      console.error('Error deleting adaptive pricing:', error);
      enqueueSnackbar('Error deleting adaptive pricing. Please try again.', {
        variant: 'error'
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{ zIndex: 1400 }}
    >
      <BootstrapDialogTitle onClose={handleClose}>
        {isEdit ? 'Edit Price Point' : 'Add Price Point'}
      </BootstrapDialogTitle>

      <DialogContent dividers>
        <ArgonBox>
          <ArgonTypography variant="body2" color="text" mb={3}>
            {isEdit
              ? 'Update the price point details below. It will be applied when EITHER the registration count is reached OR the date arrives.'
              : 'Set a price that will be applied when EITHER the registration count is reached OR the date arrives.'}
          </ArgonTypography>

          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Tooltip
                  title={
                    isFirstEntry
                      ? 'The first tier must start from 0 registrations (base price). Subsequent tiers can have different starting points.'
                      : ''
                  }
                  placement="top"
                  arrow
                >
                  <span style={{ display: 'block' }}>
                    <FormikNumberInput
                      id="fromRegistrations"
                      name="fromRegistrations"
                      label="From Registration"
                      formik={formik}
                      size="small"
                      integerLimit={10}
                      decimalLimit={0}
                      disabled={isFirstEntry}
                      placeholder={isFirstEntry ? 'Base tier (fixed at 0)' : ''}
                    />
                  </span>
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={3}>
                <ArgonBox mt={0.9} />
                <Tooltip
                  title={
                    isFirstTier
                      ? 'Dates are not applicable for the first tier (base price). Dates can be used for subsequent price points to create time-based pricing changes.'
                      : ''
                  }
                  placement="top"
                  arrow
                >
                  <span style={{ display: 'block' }}>
                    <ArgonDatePicker
                      id="fromDate"
                      name="fromDate"
                      label="From Date"
                      value={formik.values.fromDate}
                      onChange={(date) => {
                        console.log('Date picker onChange:', {
                          date,
                          fromRegistrations: formik.values.fromRegistrations,
                          hasZeroRegistrationTier
                        });

                        // Prevent date selection for first tier
                        if (isFirstTier) {
                          console.log('Date selection blocked for first tier');
                          return;
                        }

                        // Only clear the date if the conflict condition is met AND we're not editing an existing item
                        const shouldClearDate =
                          formik.values.fromRegistrations === 0 &&
                          hasZeroRegistrationTier &&
                          !isEdit; // Don't auto-clear dates when editing existing items

                        console.log('Should clear date?', {
                          shouldClearDate,
                          fromRegistrations: formik.values.fromRegistrations,
                          hasZeroRegistrationTier,
                          isEdit
                        });

                        if (shouldClearDate) {
                          formik.setFieldValue('fromDate', null);
                          return;
                        }

                        const selectedDate = date[0] || null;
                        console.log('Setting date to:', selectedDate);
                        formik.setFieldValue('fromDate', selectedDate);
                      }}
                      options={{
                        allowInput:
                          !isFirstTier &&
                          !(
                            formik.values.fromRegistrations === 0 &&
                            hasZeroRegistrationTier
                          ),
                        enableTime: false,
                        disabled:
                          isFirstTier ||
                          (formik.values.fromRegistrations === 0 &&
                            hasZeroRegistrationTier)
                      }}
                      input={{
                        size: 'small',
                        placeholder: isFirstTier
                          ? 'Not applicable for first tier'
                          : formik.values.fromRegistrations === 0 &&
                            hasZeroRegistrationTier
                          ? 'Disabled - conflicts with existing tier'
                          : 'Select date',
                        disabled:
                          isFirstTier ||
                          (formik.values.fromRegistrations === 0 &&
                            hasZeroRegistrationTier),
                        error:
                          formik.touched.fromDate &&
                          Boolean(formik.errors.fromDate),
                        helperText:
                          formik.touched.fromDate && formik.errors.fromDate
                      }}
                    />
                  </span>
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormikCurrencyInput
                  id="registrationFee"
                  name="registrationFee"
                  label="Registration Fee"
                  formik={formik}
                  size="small"
                  currency={currency}
                  language={i18n.language}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <ArgonBox>
                  <ArgonBox
                    mb={1}
                    ml={0.5}
                    lineHeight={0}
                    display="inline-block"
                  >
                    <ArgonTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Platform Fee
                    </ArgonTypography>
                  </ArgonBox>
                  <ArgonCurrencyInput
                    id="platformFee"
                    name="platformFee"
                    size="small"
                    intlConfig={{
                      locale: i18n.language,
                      currency: currency
                    }}
                    disabled
                    allowNegativeValue={false}
                    placeholder=""
                    defaultValue={0}
                    decimalsLimit={2}
                    value={calculatedSystemFee || 0}
                  />
                </ArgonBox>
              </Grid>
            </Grid>

            {/* Alert for date field disabled due to conflict */}
            {formik.values.fromRegistrations === 0 &&
              hasZeroRegistrationTier && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <strong>Date field disabled:</strong> You cannot set a date
                  when registration count is 0 and another tier already starts
                  from 0 registrations. This would create conflicting price
                  points for the first registration.
                </Alert>
              )}

            {/* Critical validation errors that block submission */}
            {formik.touched.fromRegistrations &&
              formik.errors.fromRegistrations &&
              (formik.errors.fromRegistrations.includes(
                'Another tier already uses'
              ) ||
                formik.errors.fromRegistrations.includes(
                  'A price point with this exact registration count and date combination already exists'
                )) && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <strong>Duplicate Error:</strong>{' '}
                  {formik.errors.fromRegistrations}
                </Alert>
              )}

            {/* Warning validation alerts that don't block submission */}
            {formik.touched.fromRegistrations &&
              formik.errors.fromRegistrations &&
              formik.errors.fromRegistrations.includes('Warning:') && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <strong>Tier Effectiveness Warning:</strong>{' '}
                  {formik.errors.fromRegistrations.replace('Warning: ', '')}
                  <br />
                  <em>
                    You can still save this tier, but it may never be activated.
                  </em>
                </Alert>
              )}

            {/* Date validation alerts */}
            {formik.touched.fromDate &&
              formik.errors.fromDate &&
              formik.errors.fromDate.includes('Warning:') && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <strong>Date Warning:</strong>{' '}
                  {formik.errors.fromDate.replace('Warning: ', '')}
                  <br />
                  <em>Consider using a future date for effective pricing.</em>
                </Alert>
              )}

            {/* Critical date conflict errors */}
            {formik.touched.fromDate &&
              formik.errors.fromDate &&
              !formik.errors.fromDate.includes('Warning:') && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <strong>Date Conflict:</strong> {formik.errors.fromDate}
                </Alert>
              )}

            {/* Validation alert for missing registration count or date */}
            {formik.touched.fromRegistrations &&
              formik.errors.fromRegistrations &&
              formik.errors.fromRegistrations.includes(
                'Either registration count or date'
              ) && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <strong>Required:</strong> You must specify either a
                  registration count or a date (or both). This determines when
                  the price point becomes active.
                </Alert>
              )}

            <Alert severity="info" sx={{ mt: 2 }}>
              From registration "0" means that the price point will be applied
              to the first registration. From registration "10" means that the
              price point will be applied after 10 participants have been
              registered, and so on. You can build up tiers for adaptive
              pricing. If you set the date, the price point will be applied from
              the date onwards no matter how many registrations have been made.
            </Alert>

            <Alert severity="info" sx={{ mt: 2 }}>
              Due to timing issues it may happen that the price points are not
              sharply applied when multiple participants are registering at the
              same time.
            </Alert>
          </form>
        </ArgonBox>
      </DialogContent>

      <DialogActions>
        <ArgonBox width="100%" display="flex" justifyContent="space-between">
          <ArgonBox display="flex" gap={1}>
            {isEdit && onDelete && (
              <ArgonButton
                variant="outlined"
                color="error"
                size="small"
                onClick={handleDelete}
              >
                Delete
              </ArgonButton>
            )}
          </ArgonBox>

          <ArgonBox display="flex" gap={1}>
            <ArgonButton
              variant="outlined"
              color="secondary"
              size="small"
              onClick={handleClose}
            >
              Cancel
            </ArgonButton>

            <ArgonButton
              variant="gradient"
              color="success"
              size="small"
              onClick={formik.submitForm}
              disabled={(() => {
                // Allow submission if errors are only warnings
                const hasBlockingErrors =
                  formik.errors.fromRegistrations &&
                  (formik.errors.fromRegistrations.includes(
                    'Another tier already uses'
                  ) ||
                    formik.errors.fromRegistrations.includes(
                      'Either registration count or date'
                    ) ||
                    formik.errors.fromRegistrations.includes(
                      'A price point with this exact registration count and date combination already exists'
                    ));
                const hasBlockingDateErrors =
                  formik.errors.fromDate &&
                  !formik.errors.fromDate.includes('Warning:');
                const hasRegistrationFeeErrors = formik.errors.registrationFee;

                return (
                  hasBlockingErrors ||
                  hasBlockingDateErrors ||
                  hasRegistrationFeeErrors ||
                  formik.isSubmitting
                );
              })()}
              loading={formik.isSubmitting}
            >
              {isEdit ? 'Update' : 'Add'} Price Point
            </ArgonButton>
          </ArgonBox>
        </ArgonBox>
      </DialogActions>
    </Dialog>
  );
}

SingleAdaptivePricingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  existingData: PropTypes.object,
  currency: PropTypes.string,
  defaultPrice: PropTypes.number,
  isEdit: PropTypes.bool,
  existingPricePoints: PropTypes.array,
  event: PropTypes.object,
  eventInstanceId: PropTypes.string
};
